/**
 * Input Manager - Touch/Pointer Handling
 *
 * Unified input system for:
 * - Touch input (mobile)
 * - Mouse/Pointer input (desktop)
 * - Swipe/gesture detection
 * - Energy accumulation tracking
 */

import { EventEmitter, InputEvents } from '../core/EventEmitter';

export interface SwipeData {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  deltaX: number;
  deltaY: number;
  distance: number;
  direction: { x: number; y: number };
  velocity: number;
  duration: number;
}

export interface GestureConfig {
  swipeThreshold?: number;      // Min distance for swipe (default: 50px)
  swipeVelocityThreshold?: number; // Min velocity (default: 0.3 px/ms)
  holdThreshold?: number;       // Time for hold gesture (default: 500ms)
}

interface ActiveTouch {
  id: number;
  startX: number;
  startY: number;
  startTime: number;
  currentX: number;
  currentY: number;
  lastX: number;
  lastY: number;
  lastTime: number;
  totalDistance: number;  // For energy accumulation
}

export class InputManager extends EventEmitter<InputEvents> {
  private element: HTMLElement;
  private activeTouches: Map<number, ActiveTouch> = new Map();
  private isPointerDown = false;
  private pointerData: ActiveTouch | null = null;

  // (cachedRect removed — getRect() now always returns fresh value
  // to handle singleton renderer canvas reparenting)

  // Bound handlers (for removal)
  private boundHandlers: {
    touchStart: (e: TouchEvent) => void;
    touchMove: (e: TouchEvent) => void;
    touchEnd: (e: TouchEvent) => void;
    pointerDown: (e: PointerEvent) => void;
    pointerMove: (e: PointerEvent) => void;
    pointerUp: (e: PointerEvent) => void;
    contextMenu: (e: Event) => void;
    resize: () => void;
  };

  // Track if window listeners are attached (for drag outside element)
  private windowListenersAttached = false;

  constructor(element: HTMLElement, _config: GestureConfig = {}) {
    super();

    this.element = element;

    // Bind handlers
    this.boundHandlers = {
      touchStart: this.handleTouchStart.bind(this),
      touchMove: this.handleTouchMove.bind(this),
      touchEnd: this.handleTouchEnd.bind(this),
      pointerDown: this.handlePointerDown.bind(this),
      pointerMove: this.handlePointerMove.bind(this),
      pointerUp: this.handlePointerUp.bind(this),
      contextMenu: (e: Event) => e.preventDefault(),
      resize: this.handleResize.bind(this),
    };

    this.setupListeners();
  }

  /**
   * Get bounding rect — always fresh.
   * With a singleton renderer the canvas can be reparented between containers
   * (login → game) without triggering a window resize, so a stale cached rect
   * would offset all touch/pointer coordinates.
   */
  private getRect(): DOMRect {
    return this.element.getBoundingClientRect();
  }

  /**
   * Handle window resize (kept for potential future use)
   */
  private handleResize(): void {
    // no-op: getRect() now always returns a fresh value
  }

  private setupListeners(): void {
    const el = this.element;
    const opts: AddEventListenerOptions = { passive: false };

    // Touch events (mobile)
    el.addEventListener('touchstart', this.boundHandlers.touchStart, opts);
    el.addEventListener('touchmove', this.boundHandlers.touchMove, opts);
    el.addEventListener('touchend', this.boundHandlers.touchEnd, opts);
    el.addEventListener('touchcancel', this.boundHandlers.touchEnd, opts);

    // Pointer events (desktop) - only pointerdown on element
    // Move and up are attached to window during drag to track outside element
    el.addEventListener('pointerdown', this.boundHandlers.pointerDown, opts);

    // Prevent context menu
    el.addEventListener('contextmenu', this.boundHandlers.contextMenu);

    // Listen for resize to invalidate rect cache
    window.addEventListener('resize', this.boundHandlers.resize);

    // Prevent default touch behaviors
    el.style.touchAction = 'none';
    el.style.userSelect = 'none';
  }

  /**
   * Attach pointer move/up listeners to window (for tracking drag outside element)
   */
  private attachWindowListeners(): void {
    if (this.windowListenersAttached) return;
    const opts: AddEventListenerOptions = { passive: false };
    window.addEventListener('pointermove', this.boundHandlers.pointerMove, opts);
    window.addEventListener('pointerup', this.boundHandlers.pointerUp, opts);
    window.addEventListener('pointercancel', this.boundHandlers.pointerUp, opts);
    this.windowListenersAttached = true;
  }

  /**
   * Detach pointer move/up listeners from window
   */
  private detachWindowListeners(): void {
    if (!this.windowListenersAttached) return;
    window.removeEventListener('pointermove', this.boundHandlers.pointerMove);
    window.removeEventListener('pointerup', this.boundHandlers.pointerUp);
    window.removeEventListener('pointercancel', this.boundHandlers.pointerUp);
    this.windowListenersAttached = false;
  }

  // ============================================================================
  // TOUCH HANDLERS
  // ============================================================================

  private handleTouchStart(e: TouchEvent): void {
    e.preventDefault();

    for (const touch of Array.from(e.changedTouches)) {
      const rect = this.getRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      const now = performance.now();

      this.activeTouches.set(touch.identifier, {
        id: touch.identifier,
        startX: x,
        startY: y,
        startTime: now,
        currentX: x,
        currentY: y,
        lastX: x,
        lastY: y,
        lastTime: now,
        totalDistance: 0,
      });

      this.emit('input:touch_start', { x, y, id: touch.identifier });
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    e.preventDefault();

    for (const touch of Array.from(e.changedTouches)) {
      const data = this.activeTouches.get(touch.identifier);
      if (!data) continue;

      const rect = this.getRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      const dx = x - data.lastX;
      const dy = y - data.lastY;
      const moveDistance = Math.sqrt(dx * dx + dy * dy);

      data.totalDistance += moveDistance;
      data.lastX = data.currentX;
      data.lastY = data.currentY;
      data.currentX = x;
      data.currentY = y;
      data.lastTime = performance.now();

      this.emit('input:touch_move', { x, y, id: touch.identifier, dx, dy });
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    e.preventDefault();

    for (const touch of Array.from(e.changedTouches)) {
      const data = this.activeTouches.get(touch.identifier);
      if (!data) continue;

      const rect = this.getRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;

      this.emit('input:touch_end', { x, y, id: touch.identifier });
      this.activeTouches.delete(touch.identifier);
    }
  }

  // ============================================================================
  // POINTER HANDLERS (Desktop)
  // ============================================================================

  private handlePointerDown(e: PointerEvent): void {
    // Ignore if touch (handled by touch events)
    if (e.pointerType === 'touch') return;

    e.preventDefault();

    const rect = this.getRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const now = performance.now();

    this.isPointerDown = true;
    this.pointerData = {
      id: 0,
      startX: x,
      startY: y,
      startTime: now,
      currentX: x,
      currentY: y,
      lastX: x,
      lastY: y,
      lastTime: now,
      totalDistance: 0,
    };

    // Attach window listeners to track drag even outside element
    this.attachWindowListeners();

    this.emit('input:pointer_down', { x, y, button: e.button });
  }

  private handlePointerMove(e: PointerEvent): void {
    if (e.pointerType === 'touch') return;

    const rect = this.getRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (this.isPointerDown && this.pointerData) {
      const dx = x - this.pointerData.lastX;
      const dy = y - this.pointerData.lastY;
      const moveDistance = Math.sqrt(dx * dx + dy * dy);

      this.pointerData.totalDistance += moveDistance;
      this.pointerData.lastX = this.pointerData.currentX;
      this.pointerData.lastY = this.pointerData.currentY;
      this.pointerData.currentX = x;
      this.pointerData.currentY = y;
      this.pointerData.lastTime = performance.now();

      this.emit('input:pointer_move', { x, y, dx, dy });
    }
  }

  private handlePointerUp(e: PointerEvent): void {
    if (e.pointerType === 'touch') return;
    if (!this.isPointerDown) return;

    e.preventDefault();

    const rect = this.getRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    this.emit('input:pointer_up', { x, y, button: e.button });

    this.isPointerDown = false;
    this.pointerData = null;

    // Detach window listeners when drag ends
    this.detachWindowListeners();
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get active touch/pointer data
   */
  getActiveInput(): ActiveTouch | null {
    // Prefer touch input
    if (this.activeTouches.size > 0) {
      return this.activeTouches.values().next().value ?? null;
    }
    return this.pointerData;
  }

  /**
   * Get swipe data from active input
   */
  getSwipeData(): SwipeData | null {
    const input = this.getActiveInput();
    if (!input) return null;

    const dx = input.currentX - input.startX;
    const dy = input.currentY - input.startY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const duration = performance.now() - input.startTime;
    const velocity = duration > 0 ? distance / duration : 0;

    return {
      startX: input.startX,
      startY: input.startY,
      currentX: input.currentX,
      currentY: input.currentY,
      deltaX: dx,
      deltaY: dy,
      distance,
      direction: distance > 0
        ? { x: dx / distance, y: dy / distance }
        : { x: 0, y: 0 },
      velocity,
      duration,
    };
  }

  /**
   * Get accumulated movement distance (for energy system)
   */
  getTotalDistance(): number {
    const input = this.getActiveInput();
    return input?.totalDistance ?? 0;
  }

  /**
   * Check if any input is active
   */
  isActive(): boolean {
    return this.activeTouches.size > 0 || this.isPointerDown;
  }

  /**
   * Get number of active touches
   */
  getTouchCount(): number {
    return this.activeTouches.size;
  }

  /**
   * Convert screen coordinates to normalized device coordinates (-1 to 1)
   */
  screenToNDC(x: number, y: number): { x: number; y: number } {
    const rect = this.getRect();
    return {
      x: (x / rect.width) * 2 - 1,
      y: -(y / rect.height) * 2 + 1,
    };
  }

  /**
   * Clean up
   */
  dispose(): void {
    const el = this.element;

    el.removeEventListener('touchstart', this.boundHandlers.touchStart);
    el.removeEventListener('touchmove', this.boundHandlers.touchMove);
    el.removeEventListener('touchend', this.boundHandlers.touchEnd);
    el.removeEventListener('touchcancel', this.boundHandlers.touchEnd);
    el.removeEventListener('pointerdown', this.boundHandlers.pointerDown);
    el.removeEventListener('contextmenu', this.boundHandlers.contextMenu);
    window.removeEventListener('resize', this.boundHandlers.resize);

    // Clean up window listeners if still attached
    this.detachWindowListeners();

    this.removeAllListeners();
    this.activeTouches.clear();
    this.pointerData = null;
  }
}
